// src/lib/use-scroll.ts

'use client'

import { useState, useCallback, useEffect } from 'react'

/**
 * Hook for tracking scroll position
 * @param {number} threshold - The scroll position threshold in pixels
 * @param {Object} options - Additional options for the scroll handler
 * @param {boolean} [options.passive=true] - Whether to use passive event listeners
 * @returns {boolean} Whether the element has scrolled beyond the threshold
 */
export default function useScroll(
  threshold = 0,
  options = { passive: true }
): boolean {
  const [scrolled, setScrolled] = useState(false)

  // Memoize the scroll handler if it needs to be used elsewhere
  const handleScroll = useCallback(() => {
    // Use requestAnimationFrame to debounce scroll updates
    requestAnimationFrame(() => {
      setScrolled(window.scrollY > threshold)
    })
  }, [threshold])

  useEffect(() => {
    // Add event listener with passive option for better scroll performance
    window.addEventListener('scroll', handleScroll, options)
    
    // Set initial state
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll, options])

  return scrolled
}
